<template>
  <div class="ncrm-staging-table">
    <!-- ── Header ──────────────────────────────────────────────────────────── -->
    <div class="ncrm-staging-table__header">
      <div>
        <div class="text-subtitle-2 font-weight-semibold ncrm-text-strong">
          {{ title }}
        </div>
        <div class="text-caption text-medium-emphasis mt-1">{{ subtitle }}</div>
      </div>
      <div class="d-flex align-center flex-wrap ga-2">
        <slot name="actions" />
      </div>
    </div>

    <!-- ── Status filter chips ─────────────────────────────────────────────── -->
    <div v-if="showFilters" class="d-flex flex-wrap ga-2 mb-3">
      <v-chip
        v-for="opt in statusFilterOptions"
        :key="opt.value"
        size="small"
        :color="activeFilter === opt.value ? 'primary' : 'default'"
        :variant="activeFilter === opt.value ? 'tonal' : 'outlined'"
        class="cursor-pointer"
        @click="setActiveFilter(opt.value)"
      >
        {{ opt.label }}
        <span v-if="opt.count !== undefined" class="ml-1 opacity-70">({{ opt.count }})</span>
      </v-chip>
    </div>

    <!-- ── Table ───────────────────────────────────────────────────────────── -->
    <v-data-table
      :headers="headers"
      :items="filteredRecords"
      item-key="id"
      density="comfortable"
      :loading="loading"
      :items-per-page="itemsPerPage"
      class="ncrm-staging-table__table"
      :row-props="() => ({ class: 'ncrm-staging-table__row' })"
      @click:row="(_, row) => $emit('select', row.item)"
    >
      <template #item.external_id="{ item }">
        <span class="font-weight-medium text-body-2">{{ item.external_id }}</span>
      </template>

      <template #item.name="{ item }">
        <span class="text-body-2">{{ item.name || '—' }}</span>
      </template>

      <template #item.status="{ item }">
        <v-chip size="x-small" :color="statusChip(item.status).color" :variant="statusChip(item.status).variant">
          {{ $t(`ncrm.workflows.staging.status.${item.status}`) }}
        </v-chip>
      </template>

      <template #item.mapping_status="{ item }">
        <v-chip
          v-if="item.mapping_status"
          size="x-small"
          :color="mappingChip(item.mapping_status).color"
          :variant="mappingChip(item.mapping_status).variant"
        >
          {{ $t(`ncrm.workflows.staging.mappingStatus.${item.mapping_status}`) }}
        </v-chip>
        <span v-else>—</span>
      </template>

      <template #item.dedup_score="{ item }">
        <span v-if="item.dedup_score !== null && item.dedup_score !== undefined">
          <v-chip size="x-small" :color="dedupScoreColor(item.dedup_score)">
            {{ item.dedup_score }}%
          </v-chip>
        </span>
        <span v-else class="text-medium-emphasis text-caption">—</span>
      </template>

      <template #item.source_kind="{ item }">
        <v-chip
          v-if="item.source_kind === 'file'"
          size="x-small"
          color="info"
        >
          <v-icon start size="12">mdi-file-document-outline</v-icon>
          {{ $t('ncrm.workflows.staging.sourceKindFile') }}
        </v-chip>
        <v-chip v-else size="x-small" color="primary">
          <v-icon start size="12">mdi-api</v-icon>
          {{ $t('ncrm.workflows.staging.sourceKindApi') }}
        </v-chip>
      </template>

      <template #item.endpoint_name="{ item }">
        <span class="text-caption text-medium-emphasis">
          {{ item.source_kind === 'file'
              ? (item.file_import_name || item.source_system || '—')
              : (item.endpoint_name || item.source_system || '—') }}
        </span>
      </template>

      <template #item.created_at="{ item }">
        <span class="text-caption text-medium-emphasis">{{ formatDate(item.created_at) }}</span>
      </template>

      <template #item.actions="{ item }">
        <v-btn
          v-if="rowSecondaryActionLabel && canUseSecondaryAction(item)"
          size="small"
          variant="tonal"
          :color="rowSecondaryActionColor"
          :disabled="rowSecondaryActionDisabled"
          class="mr-1"
          :title="rowSecondaryActionLabel"
          @click.stop="$emit('row-secondary-action', item)"
        >
          {{ rowSecondaryActionLabel }}
        </v-btn>
        <v-btn
          size="small"
          variant="text"
          color="primary"
          :title="rowActionLabel || $t('ncrm.workflows.staging.actions.view')"
          @click.stop="$emit('select', item)"
        >
          {{ rowActionLabel || $t('ncrm.workflows.staging.actions.view') }}
        </v-btn>
      </template>

      <template #no-data>
        <div class="py-8 text-center text-body-2 text-medium-emphasis">
          {{ emptyText }}
        </div>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { formatDisplayDateTime } from "@/Utile/displayFormat.mjs";
export default {
  name: "NcrmWorkflowStagingTable",
  props: {
    records: { type: Array, default: () => [] },
    counts: { type: Object, default: () => ({}) },
    loading: { type: Boolean, default: false },
    title: { type: String, default: "" },
    subtitle: { type: String, default: "" },
    emptyText: { type: String, default: "" },
    showFilters: { type: Boolean, default: true },
    itemsPerPage: { type: Number, default: 10 },
    /**
     * Pre-filtrează după sursă: 'api' / 'file' / null (toate).
     * Când e setat, ascunde și coloana de chip „Sursă" (redundantă).
     */
    sourceKindFilter: { type: String, default: null },
    statusScope: { type: Array, default: () => [] },
    rowActionLabel: { type: String, default: "" },
    rowSecondaryActionLabel: { type: String, default: "" },
    rowSecondaryActionDisabled: { type: Boolean, default: false },
    rowSecondaryActionEnabledStatuses: { type: Array, default: () => [] },
    rowSecondaryActionColor: { type: String, default: "info" },
  },
  emits: ["select", "row-secondary-action", "filter-change"],
  data() {
    return { activeFilter: "all" };
  },
  computed: {
    /**
     * Records pre-filtrate după sourceKindFilter (dacă e setat).
     * Toate celelalte computed-uri (chips de status, filteredRecords)
     * lucrează deja peste această sursă.
     */
    sourceFilteredRecords() {
      let records = this.records;
      if (this.sourceKindFilter) {
        records = records.filter((r) => r.source_kind === this.sourceKindFilter);
      }
      if (Array.isArray(this.statusScope) && this.statusScope.length) {
        const allowed = new Set(this.statusScope);
        records = records.filter((r) => allowed.has(r.status));
      }
      return records;
    },
    statusFilterOptions() {
      const records = this.sourceFilteredRecords;
      const total = records.length;
      const statuses = [
        "pending_dedup",
        "duplicate_found",
        "pending_enrichment",
        "enriched_pending_dedup",
        "pending_review",
        "approved",
        "rejected",
        "written",
        "written_new_company",
        "written_existing_company",
        "lead_board_added",
        "lead_board_already_exists",
        "lead_board_error",
      ];
      return [
        { value: "all", label: this.$t("ncrm.workflows.staging.filters.all"), count: total },
        ...statuses
          .filter((s) => records.some((r) => r.status === s))
          .map((s) => ({
            value: s,
            label: this.$t(`ncrm.workflows.staging.status.${s}`),
            count: records.filter((r) => r.status === s).length,
          })),
      ];
    },
    filteredRecords() {
      const base = this.sourceFilteredRecords;
      if (this.activeFilter === "all") return base;
      return base.filter((r) => r.status === this.activeFilter);
    },
    headers() {
      const cols = [
        { title: this.$t("ncrm.workflows.staging.headers.externalId"), key: "external_id", sortable: false },
        { title: this.$t("ncrm.workflows.staging.headers.name"), key: "name", sortable: false },
        { title: this.$t("ncrm.workflows.staging.headers.status"), key: "status", sortable: false },
        { title: this.$t("ncrm.workflows.staging.headers.mappingStatus"), key: "mapping_status", sortable: false },
        { title: this.$t("ncrm.workflows.staging.headers.dedupScore"), key: "dedup_score", sortable: false },
      ];
      // Chip-ul „Sursă" (API / Fișier) e redundant când lista deja conține
      // doar un singur tip — îl ascundem.
      if (!this.sourceKindFilter) {
        cols.push({ title: this.$t("ncrm.workflows.staging.headers.sourceKind"), key: "source_kind", sortable: false, width: 110 });
      }
      cols.push(
        { title: this.$t("ncrm.workflows.staging.headers.source"), key: "endpoint_name", sortable: false },
        { title: this.$t("ncrm.workflows.staging.headers.addedAt"), key: "created_at", sortable: false },
        { title: "", key: "actions", sortable: false, width: this.rowSecondaryActionLabel ? 190 : 80 }
      );
      return cols;
    },
  },
  watch: {
    statusFilterOptions: {
      immediate: true,
      handler(options) {
        if (!options.some((option) => option.value === this.activeFilter)) {
          this.setActiveFilter("all");
        }
      },
    },
  },
  methods: {
    canUseSecondaryAction(item) {
      if (!this.rowSecondaryActionEnabledStatuses.length) return true;
      return this.rowSecondaryActionEnabledStatuses.includes(item?.status);
    },
    setActiveFilter(value) {
      this.activeFilter = value || "all";
      this.$emit("filter-change", this.activeFilter);
    },
    statusChip(status) {
      const map = {
        pending_dedup:      { color: "neutral",  variant: "tonal" },
        duplicate_found:    { color: "warning",  variant: "tonal" },
        pending_enrichment: { color: "info",     variant: "tonal" },
        enriched_pending_dedup: { color: "info", variant: "tonal" },
        pending_review:     { color: "warning",  variant: "tonal" },
        approved:           { color: "success",  variant: "tonal" },
        rejected:           { color: "error",    variant: "tonal" },
        written:            { color: "success",  variant: "flat"  },
        written_new_company: { color: "success", variant: "flat" },
        written_existing_company: { color: "success", variant: "tonal" },
        lead_board_added: { color: "success", variant: "flat" },
        lead_board_already_exists: { color: "info", variant: "tonal" },
        lead_board_error: { color: "error", variant: "tonal" },
      };
      return map[status] || { color: "neutral", variant: "tonal" };
    },
    mappingChip(status) {
      return {
        auto_mapped:       { color: "neutral",  variant: "tonal" },
        needs_review:      { color: "warning",  variant: "tonal" },
        mapping_confirmed: { color: "success",  variant: "tonal" },
      }[status] || { color: "neutral", variant: "tonal" };
    },
    dedupScoreColor(score) {
      if (score >= 95) return "success";
      if (score >= 70) return "warning";
      return "error";
    },
    formatDate(value) {
      if (!value) return "—";
      return formatDisplayDateTime(value);
    },
  },
};
</script>

<style scoped>
.ncrm-staging-table__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 16px 18px 12px;
}
.ncrm-staging-table__table :deep(.ncrm-staging-table__row) {
  cursor: pointer;
}
.ncrm-staging-table__table :deep(.ncrm-staging-table__row:hover td) {
  background-color: rgba(var(--v-theme-primary), 0.04);
}
</style>
